import React, { useEffect, useState } from "react";
import style from "./AdminAddCompany.module.css";
import PageLayout from "../../components/PageLayout/PageLayout";
import { FieldArray, Form, Formik, useFormikContext } from "formik";
import FormikField from "../../stuffs/FormikField/FormikField";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { NavLink, useHistory } from "react-router-dom";
import { LatLngTuple } from "leaflet";
import FormikSelect from "../../stuffs/FormikSelect/FormikSelect/FormikSelect";
import OneOffer from "../../components/MainContent/Body/List/OneOffer/OneOffer";
import { icons } from "../../stuffs/Icons";
import { searchIcon } from "../../stuffs/SearchIcon";
import { Button, FormControlLabel, Switch } from "@mui/material";
import moment from "moment";
import { addNewCompanyAsAdmin } from "../../slices/company/companySlice";
import { adminLogInHelper } from "../../helpers/helpers";
import PaymentModal from "../../stuffs/PaymentModal/PaymentModal";
import axios from "axios";
import { isValidNip } from "../../helpers/nipHelper";
import { formatZipCode } from "../../helpers/zipCodeHelper";

export interface selectValuesAndLabels {
  value: string | number;
  label: string;
}

interface RecommendedWatcherProps {
  values: FormValues;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => void;
  setRecommendedError: React.Dispatch<React.SetStateAction<string>>;
}

interface FormValues {
  _id?: string;
  user: string;
  shortName: string;
  name: string;
  website: string;
  email: string;
  phone: string;
  NIP: string;
  description: string;
  mainField: Array<string>;
  coordinates: LatLngTuple;
  city: string;
  street: string;
  streetNumber: string;
  localNumber?: string | null;
  zipCode: string;
  voivodeship: string;
  isInternational: boolean;
  isADR: boolean;
  addDate: string;
  expirationDate: string;
  period: number;
  isHighlighted: boolean;
  isRecommended: boolean;
  isOversized: boolean;
  newPeriod: number;
  newIsHighlighted: boolean;
  newIsRecommended: boolean;
  newExpirationDate: string;

  price: number;
}

const initialValues: FormValues = {
  user: "",
  shortName: "",
  name: "",
  website: "",
  email: "",
  phone: "",
  NIP: "",
  description: "",
  mainField: [],
  coordinates: [1, 1],
  city: "",
  street: "",
  streetNumber: "",
  localNumber: "",
  zipCode: "",
  voivodeship: "",
  isInternational: false,
  isADR: false,
  isOversized: false,
  addDate: "",
  expirationDate: "",
  period: 1,

  isHighlighted: false,
  isRecommended: false,

  newPeriod: 1,
  newIsHighlighted: false,
  newIsRecommended: false,
  newExpirationDate: "",

  price: 0,
};

const RecommendedWatcher: React.FC<RecommendedWatcherProps> = ({
  values,
  setFieldValue,
  setRecommendedError,
}) => {
  const mainField = values.mainField?.[0];
  const voivodeship = values.voivodeship;

  useEffect(() => {
    if (!values.isRecommended) return;

    setFieldValue("isRecommended", false);
    setFieldValue("isHighlighted", false);

    setRecommendedError(
      "Zmienił się główny pojazd lub województwo. Sprawdź dostępność TOP 3 ponownie."
    );
  }, [mainField, voivodeship]);

  useEffect(() => {
    if (
      typeof mainField === "string" &&
      mainField !== "" &&
      voivodeship &&
      !values.isRecommended
    ) {
      setRecommendedError("");
    }
  }, [
    mainField,
    voivodeship,
    values.isRecommended,
    setRecommendedError,
  ]);

  return null;
};
const AddCompanySchema = Yup.object().shape({
  shortName: Yup.string().required("Pole wymagane"),
  name: Yup.string().required("Pole wymagane"),
  website: Yup.string(),
  email: Yup.string()
    .email("Wymagany poprawny adres email!")
    .required("Pole wymagane"),
  phone: Yup.string()
    .required("Pole wymagane")
    .matches(/^[0-9]+$/, "Muszą być tylko cyfry"),
  NIP: Yup.string()
    .required("Pole wymagane")
    .matches(/^[0-9]+$/, "Muszą być tylko cyfry")
    .min(10, "Musi być dokładnie 10 cyfr")
    .max(10, "Musi być dokładnie 10 cyfr"),
  description: Yup.string()
    .required("Pole wymagane")
    .max(500, "Opis musi mieć maksymalnie 500 znaków"),
  mainField: Yup.array()
    .min(1, "Musi być conajmniej pojazd")
    .required("Wymagane pojazdy"),
  city: Yup.string().required("Pole wymagane"),
  street: Yup.string(),
  streetNumber: Yup.string()
    .required("Pole wymagane"),
  localNumber: Yup.string(),
  zipCode: Yup.string()
    .required("Kod pocztowy jest wymagany")
    .matches(/^\d{2}-\d{3}$/, "Kod pocztowy musi mieć format XX-XXX"),
  voivodeship: Yup.string().required("Pole wymagane"),
  user: Yup.string().email("Wymagany poprawny email użytkownika").required("Pole wymagane")

});

const fetchCompanyData = async (
  nip: string,
  setFieldValue: (field: string, value: any) => void,
  setFieldError: (field: string, message?: string) => void
) => {
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      setFieldError("NIP", "Brak tokena użytkownika.");
      return;
    }

    const response = await axios.get(
      `/api/company-data/${nip}`,
      {
        headers: {
          "x-auth-token": token,
        },
      }
    );

    const company = response.data;

    setFieldError("NIP", undefined);

    setFieldValue("name", company.name || "");
    setFieldValue("city", company.city || "");
    setFieldValue("street", company.street || "");
    setFieldValue("streetNumber", company.streetNumber || "");
    setFieldValue("localNumber", company.localNumber || "");
    setFieldValue("zipCode", company.zipCode || "");
    setFieldValue("voivodeship", company.voivodeship || "");

  } catch (error) {
    if (axios.isAxiosError(error)) {
      setFieldError(
        "NIP",
        error.response?.data?.error ||
          "Nie udało się pobrać danych firmy z CEIDG."
      );
    } else {
      setFieldError("NIP", "Nieznany błąd.");
    }
  }
};

const AdminAddCompany: React.FC = () => {

  
  let us: { email: string };

  const dispatch = useDispatch();
  const history = useHistory();
  const isAdminLogIn = adminLogInHelper();

  const [showModalPrice, setshowModalPrice] = useState(false);
  const [price, setPrice] = useState(0);

  const handleCloseModalPrice = () => setshowModalPrice(false);
  const handleShowModalPrice = () => setshowModalPrice(true);

  const [isCheckingRecommended, setIsCheckingRecommended] = useState(false);
  const [recommendedError, setRecommendedError] = useState("");


  const priceContract = [0, 49, 69];
  const highlightPricePerYear = 150;
  const recommendedPricePerYear = 300;

const calculatePrice = (values: FormValues) =>
  priceContract[values.period - 1] +
  (values.isRecommended
    ? recommendedPricePerYear * values.period
    : values.isHighlighted
    ? highlightPricePerYear * values.period
    : 0);

  const handleSubmit = (values: FormValues, finalPrice: number): void => {
      
    try {
      dispatch(addNewCompanyAsAdmin(values));
      history.push("/");
    } catch (e) {
      console.log(e);
    }
  };

  const vehiclesSet = [
    { label: "Cysterna", value: "Tanker" },
    { label: "Plandeka", value: "Tilt" },
    { label: "Kontener", value: "Container" },
    { label: "HDS", value: "LoaderCrane" },
    { label: "Furgon", value: "Bus" },
    { label: "Drobnica", value: "Cargo" },
    { label: "Dźwig", value: "Crane" },
    { label: "Betoniarka", value: "Mixer" },
    { label: "Wywrotka", value: "Tipper" },
    { label: "Koparka", value: "Digger" },
    { label: "Silos", value: "Silo" },
    { label: "Chłodnia", value: "Refrigerated" },
    { label: "Naczepa wywrotka", value: "TipperTrailer" },
    { label: "Niskopodwoziowe", value: "LowLoader" },
    { label: "Naczepa skrzyniowa", value: "BoxTrailer" },
    { label: "Solówka / zestaw z przyczepą", value: "RigidTruck" },
    { label: "Sklep branżowy", value: "Shop" },
    { label: "Serwis i naprawa", value: "Service" },
  ];

  const vehiclesItems: Array<selectValuesAndLabels> = vehiclesSet;

  const voivodeshipSet: Array<selectValuesAndLabels> = [
    { label: "Dolnośląskie", value: "Dsl" },
    { label: "Kujawsko-Pomorskie", value: "K-P" },
    { label: "Lubelskie", value: "Lbl" },
    { label: "Lubuskie", value: "Lbu" },
    { label: "Łódzkie", value: "Ldz" },
    { label: "Mazowieckie", value: "Maz" },
    { label: "Małopolskie", value: "Mal" },
    { label: "Opolskie", value: "Opo" },
    { label: "Podlaskie", value: "Pdl" },
    { label: "Podkarpackie", value: "Pkr" },
    { label: "Pomorskie", value: "Pom" },
    { label: "Śląskie", value: "Sl" },
    { label: "Świętokrzyskie", value: "Sw" },
    { label: "Wielkopolskie", value: "Wlkp" },
    { label: "Warmińsko-Mazurskie", value: "W-M" },
    { label: "Zachodniopomorskie", value: "Zpm" },
  ];

  const period: Array<selectValuesAndLabels> = [
    { label: "1 rok (GRATIS)", value: 1 },
    { label: "2 lata", value: 2 },
    { label: "3 lata", value: 3 },
  ];

  return (
  <PageLayout className={style.AddCompany}>
            {isAdminLogIn ? (
              <>
            <h1>Dodaj nową firmę</h1>

            <div className={style.StartInfo}>
              <h3>🎁 Oferta startowa</h3>

              <p>
                Dodaj firmę do katalogu bezpłatnie na 12 miesięcy.
              </p>

              <p>
                <strong>Najpierw wpisz numer NIP</strong>, aby automatycznie
                pobrać dane firmy z dostępnych rejestrów publicznych. Pobrane
                dane mogą zastąpić wcześniej wprowadzone informacje.
              </p>

              <p>
                Pakiety 2- i 3-letnie oraz opcje premium są dostępne jako
                dodatkowe warianty publikacji.
              </p>
            </div>

                <Formik
                  initialValues={initialValues}
                  validationSchema={AddCompanySchema}
                  onSubmit={(values) => {
                    const finalPrice = calculatePrice(values);
                    setPrice(finalPrice);

                    if (finalPrice > 0) {
                      handleShowModalPrice();
                    } else {
                      handleSubmit(values, finalPrice);
                    }
                  }}
                >
                  {({ errors, touched, values, setFieldValue, setFieldError, setFieldTouched, submitCount, isValid }) => {
                    const fieldLabels: Record<string, string> = {
                      user: "Użytkownik",
                      NIP: "NIP",
                      name: "Nazwa firmy",
                      shortName: "Nazwa skrócona",
                      phone: "Telefon",
                      email: "Email",
                      zipCode: "Kod pocztowy",
                      city: "Miasto",
                      voivodeship: "Województwo",
                      streetNumber: "Numer ulicy",
                      description: "Opis działalności",
                      mainField: "Pojazdy",
                    };

                    const missingFields = Object.keys(errors).map(
                      (key) => fieldLabels[key] || key
                    );

                    return (
                      <>
                        <RecommendedWatcher
                          values={values}
                          setFieldValue={setFieldValue}
                          setRecommendedError={setRecommendedError}
                        />
                        <div className={style.Page}>
                          <div className={style.Cell1}>
                            <Form noValidate>
                              <div className={style.Form}>
                                <div className={style.Row}>
                                  <div className={style.Field}>
                                      <FormikField
                                        name="NIP"
                                        value={values.NIP}
                                        label="NIP"
                                        variant="standard"
                                        required
                                        err={errors.NIP && touched.NIP}
                                        onChange={async (e) => {
                                          const nip = e.target.value.replace(/\D/g, "").slice(0, 10);

                                          await setFieldValue("NIP", nip, false);

                                          if (nip.length < 10) {
                                            setFieldError("NIP", undefined);
                                            return;
                                          }

                                          await setFieldTouched("NIP", true, false);

                                          if (!isValidNip(nip)) {
                                            setFieldError("NIP", "Nieprawidłowy numer NIP.");
                                            return;
                                          }

                                          setFieldError("NIP", undefined);

                                          await fetchCompanyData(
                                            nip,
                                            setFieldValue,
                                            setFieldError
                                          );
                                        }}
                                      />
                                  </div>

                                  <div className={style.Field}>
                                    <FormikSelect
                                      name="period"
                                      items={period}
                                      label="Okres publikacji"
                                      err={errors.period && touched.period}
                                    />
                                  </div>
                                </div>
                                <div className={style.Row}>
                                  <div className={style.Field}>
                                    <FormikField
                                      name="user"
                                      value={values.user}
                                      label="Użytkownik"
                                      variant="standard"
                                      required
                                      err={errors.user && touched.user}
                                    />
                                  </div>
                                </div>
                                <div className={style.Row}>
                                  <div className={style.Field}>
                                    <FormikField
                                      name="name"
                                      value={values.name}
                                      label="Nazwa"
                                      variant="standard"
                                      required
                                      err={errors.name && touched.name}
                                    />
                                  </div>
                                </div>
                                <div className={style.Row}>
                                  <div className={style.Field}>
                                    <FormikField
                                      name="shortName"
                                      value={values.shortName}
                                      label="Nazwa Skrócona"
                                      variant="standard"
                                      required
                                      err={errors.shortName && touched.shortName}
                                    />
                                  </div>
                                  <div className={style.Field}>
                                    <FormikField
                                      name="phone"
                                      value={values.phone}
                                      label="Telefon"
                                      variant="standard"
                                      required
                                      err={errors.phone && touched.phone}
                                      onChange={(e) => {
                                        const phone = e.target.value.replace(/\D/g, "");
                                        setFieldValue("phone", phone);
                                      }}
                                    />
                                  </div>
                                </div>
                                <div className={style.Row}>
                                  <div className={style.Field}>
                                    <FormikField
                                      name="email"
                                      value={values.email}
                                      label="Email"
                                      variant="standard"
                                      required
                                      err={errors.email && touched.email}
                                    />
                                  </div>

                                  <div className={style.Field}>
                                    <FormikField
                                      name="website"
                                      value={values.website}
                                      label="Strona internetowa"
                                      variant="standard"
                                      err={errors.website}
                                    />
                                  </div>
                                </div>
                                <div className={style.Row}>
                                  <div className={style.Field}>
                                    <FormikField
                                      placeholder="XX-XXX"
                                      name="zipCode"
                                      onChange={(e) =>
                                        setFieldValue("zipCode", formatZipCode(e.target.value))
                                      }
                                      value={values.zipCode}
                                      label="Kod pocztowy"
                                      variant="standard"
                                      required
                                      err={errors.zipCode && touched.zipCode}
                                    />
                                  </div>

                                  <div className={style.Field}>
                                    <FormikField
                                      name="city"
                                      value={values.city}
                                      label="Miasto"
                                      variant="standard"
                                      required
                                      err={errors.city && touched.city}
                                    />
                                  </div>
                                </div>

                                <div className={style.Row}>
                                  <div className={style.Field}>
                                    <FormikSelect
                                      name="voivodeship"
                                      items={voivodeshipSet}
                                      label="Województwo"
                                      required
                                      err={
                                        errors.voivodeship &&
                                        touched.voivodeship
                                      }
                                    />
                                  </div>
                                </div>

                                <div className={style.Row}>
                                  <div className={style.Field}>
                                    <FormikField
                                      name="street"
                                      value={values.street}
                                      label="Ulica"
                                      variant="standard"
                                      err={errors.street && touched.street}
                                    />
                                  </div>

                                  <div className={style.Field}>
                                    <FormikField
                                      name="streetNumber"
                                      value={values.streetNumber}
                                      label="Numer ulicy"
                                      variant="standard"
                                      required
                                      err={
                                        errors.streetNumber &&
                                        touched.streetNumber
                                      }
                                    />
                                  </div>

                                  <div className={style.Field}>
                                    <FormikField
                                      name="localNumber"
                                      value={values.localNumber ?? ""}
                                      label="Numer lokalu"
                                      variant="standard"
                                      err={
                                        errors.localNumber &&
                                        touched.localNumber
                                      }
                                    />
                                  </div>
                                </div>

                                <div className={style.Row}>
                                  <div className={style.Field}>
                                    <FormikField
                                      name="description"
                                      value={values.description}
                                      label="Opis działalności (Maksymalnie 500 znaków)"
                                      variant="standard"
                                      required
                                      multiline
                                      rows={6}
                                      err={
                                        errors.description &&
                                        touched.description
                                      }
                                    />
                                  </div>
                                </div>

                                <div>
                                  Pojazdy<sup>*</sup>:
                                </div>
                                <div className={style.Info}>
                                    Wybierz jako pierwszy główny rodzaj pojazdu — na jego podstawie
                                    oferta zostanie przypisana do kategorii.
                                </div>
                                
                                <div className={style.ArrayField}>
                                  <FieldArray
                                    name="mainField"
                                    render={(arrayHelpers) => (
                                      <div className={style.VehicleBox}>
                                        {values.mainField.map((t, index) => (
                                          <div
                                            className={style.VehicleItem}
                                            key={index}
                                          >
                                            <FormikSelect
                                              name={`mainField[${index}]`}
                                              items={vehiclesItems}
                                              label="Wybierz pojazd"
                                              required
                                              err={
                                                errors.mainField &&
                                                touched.mainField
                                              }
                                            />

                                            <Button
                                              variant="outlined"
                                              type="button"
                                              onClick={() =>
                                                arrayHelpers.remove(index)
                                              }
                                            >
                                              x
                                            </Button>
                                          </div>
                                        ))}

                                        <Button
                                          variant="outlined"
                                          type="button"
                                          onClick={() => arrayHelpers.push("")}
                                        >
                                          +
                                        </Button>
                                      </div>
                                    )}
                                  />
                                </div>
                                <div className={style.SwitchSection}>
                                  <div className={style.SwitchSectionTitle}>
                                    Usługi
                                  </div>

                                  <div className={style.SwitchRow}>
                                    <div
                                      className={style.SwitchField}
                                      title="Zaznacz jeżeli możesz przewozić materiały niebezpieczne"
                                    >
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            name="isADR"
                                            checked={values.isADR === true}
                                            onChange={(event, checked) => {
                                              setFieldValue("isADR", checked);
                                            }}
                                          />
                                        }
                                        label="Transport ADR"
                                      />
                                    </div>

                                    <div
                                      className={style.SwitchField}
                                      title="Zaznacz jeżeli możesz przewozić materiały ponadnormatywne"
                                    >
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            name="isOversized"
                                            checked={values.isOversized === true}
                                            onChange={(event, checked) => {
                                              setFieldValue("isOversized", checked);
                                            }}
                                          />
                                        }
                                        label="Transport ponadnormatywny"
                                      />
                                    </div>

                                    <div
                                      className={style.SwitchField}
                                      title="Zaznacz jeżeli możesz świadczyć transport międzynarodowy"
                                    >
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            name="isInternational"
                                            checked={values.isInternational === true}
                                            onChange={(event, checked) => {
                                              setFieldValue(
                                                "isInternational",
                                                checked
                                              );
                                            }}
                                          />
                                        }
                                        label="Transport międzynarodowy"
                                      />
                                    </div>
                                  </div>
                                </div>

                                <div className={style.SwitchSection}>
                                  <div className={style.SwitchSectionTitle}>
                                    Promowanie
                                  </div>

                                  <div className={style.SwitchRow}>
                                    <div
                                      className={style.SwitchField}
                                      title="Zawiera wszystkie korzyści Wyróżnienia oraz gwarantowane miejsce w Top 3 w wybranej kategorii i województwie. Dopłata 300 PLN netto za każdy rozpoczęty rok."
                                    >
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            name="isRecommended"
                                            checked={values.isRecommended === true}
                                            onChange={async (event, checked) => {
                                              if (!checked) {
                                                setFieldValue(
                                                  "isRecommended",
                                                  false
                                                );
                                                setFieldValue(
                                                  "isHighlighted",
                                                  false
                                                );
                                                setRecommendedError("");
                                                return;
                                              }

                                              const mainField =
                                                values.mainField?.[0];

                                              const voivodeship =
                                                values.voivodeship;

                                              if (
                                                typeof mainField !== "string" ||
                                                mainField === "" ||
                                                !voivodeship
                                              ) {
                                                setRecommendedError(
                                                  "Najpierw wybierz główny rodzaj pojazdu i województwo."
                                                );
                                                return;
                                              }

                                              setIsCheckingRecommended(true);
                                              setRecommendedError("");

                                              try {
                                                const response = await axios.post(
                                                  "/api/offers/recommended-availability",
                                                  {
                                                    mainField,
                                                    voivodeship,
                                                  },
                                                  {
                                                    headers: {
                                                      "Content-Type":
                                                        "application/json",
                                                      "x-auth-token":
                                                        localStorage.getItem(
                                                          "token"
                                                        ) || "",
                                                    },
                                                  }
                                                );

                                                if (!response.data.available) {
                                                  setRecommendedError(
                                                    "Brak wolnych miejsc TOP 3 dla tej kategorii i województwa."
                                                  );

                                                  setFieldValue(
                                                    "isRecommended",
                                                    false
                                                  );
                                                  setFieldValue(
                                                    "isHighlighted",
                                                    false
                                                  );

                                                  return;
                                                }

                                                setFieldValue(
                                                  "isRecommended",
                                                  true
                                                );
                                                setFieldValue(
                                                  "isHighlighted",
                                                  true
                                                );
                                              } catch (err) {
                                                setRecommendedError(
                                                  "Nie udało się sprawdzić dostępności TOP 3."
                                                );

                                                setFieldValue(
                                                  "isRecommended",
                                                  false
                                                );
                                                setFieldValue(
                                                  "isHighlighted",
                                                  false
                                                );
                                              } finally {
                                                setIsCheckingRecommended(false);
                                              }
                                            }}
                                          />
                                        }
                                        label="Polecane TOP 3 (zawiera Wyróżnienie)"
                                      />

                                      {isCheckingRecommended && (
                                        <div className={style.Info}>
                                          Sprawdzanie dostępności TOP 3...
                                        </div>
                                      )}

                                      {recommendedError && (
                                        <div className={style.Error}>
                                          {recommendedError}
                                        </div>
                                      )}
                                    </div>

                                    <div
                                      className={style.SwitchField}
                                      title="Wyróżnienie ustawia wizytówkę nad ofertami standardowymi. Dopłata 150 PLN netto za każdy rozpoczęty rok."
                                    >
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            name="isHighlighted"
                                            checked={
                                              values.isHighlighted === true
                                            }
                                            onChange={(event, checked) => {
                                              setFieldValue(
                                                "isHighlighted",
                                                checked
                                              );
                                            }}
                                          />
                                        }
                                        label="Wyróżnienie"
                                        disabled={values.isRecommended}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className={style.PriceBox}>
                                  {(() => {
                                    const calculatedPrice =
                                      calculatePrice(values);

                                    return (
                                      <>
                                        CENA: {calculatedPrice} PLN netto{" : "}
                                        {(calculatedPrice * 1.23).toFixed(2)}{" "}
                                        PLN brutto (z VAT)
                                      </>
                                    );
                                  })()}
                                </div>
                                {submitCount > 0 && !isValid && (
                                  <div className={style.Error}>
                                    <strong>Nie można dodać firmy.</strong>
                                    <div>
                                      Uzupełnij lub popraw pola: {missingFields.join(", ")}
                                    </div>
                                  </div>
                                )}

                                <div className={style.Button}>
                                  <Button
                                    variant="outlined"
                                    type="submit"
                                    disabled={isCheckingRecommended}
                                  >
                                    {isCheckingRecommended ? "SPRAWDZANIE..." : "DODAJ"}
                                  </Button>
                                </div>
                              </div>
                            </Form>
                          </div>

                          <div className={style.Cell2}>
                            <div className={style.Display}>
                              
                                <OneOffer
                                  key={values.shortName}
                                  name={
                                    values.name.length > 0
                                      ? values.name
                                      : "Nazwa Firmy"
                                  }
                                  shortName={values.shortName}
                                  website={
                                    values.website.length > 0
                                      ? values.website
                                      : "www.twojastrona.pl"
                                  }
                                  email={
                                    values.email.length > 0
                                      ? values.email
                                      : "twoj@email.pl"
                                  }
                                  phone={
                                    values.phone.length > 0
                                      ? values.phone
                                      : "555555555"
                                  }
                                  NIP={
                                    values.NIP.length > 0
                                      ? values.NIP
                                      : "0000000000"
                                  }
                                  description={values.description}
                                  mainField={
                                    values.mainField.length > 0 &&
                                    values.mainField[0].length > 0
                                      ? values.mainField[0]
                                      : "Search"
                                  }
                                  isHighlighted={values.isHighlighted}
                                  isRecommended={values.isRecommended}
                                  voivodeship={values.voivodeship}
                                />
                            

                              <div className={style.Icon}>
                                Dostępny rodzaj pojazdów:
                                {values.mainField.length > 0
                                  ? values.mainField.map((e) => {
                                      const icon = icons.filter(
                                        (i) => i.routing === e
                                      );

                                      return icon.length > 0
                                        ? icon[0].svg
                                        : searchIcon[0].svg;
                                    })
                                  : ""}
                              </div>

                              <div className={style.Description}>
                                {values.description.length > 0
                                  ? values.description
                                  : "Opis działalności"}
                              </div>

                              <div className={style.ServicesPreview}>
                                <div className={style.PreviewTitle}>
                                  Usługi
                                </div>

                                <div className={style.PreviewSwitches}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        name="isADRValue"
                                        disabled
                                        checked={values.isADR === true}
                                      />
                                    }
                                    label="ADR"
                                  />

                                  <FormControlLabel
                                    control={
                                      <Switch
                                        name="isOversizedValue"
                                        disabled
                                        checked={values.isOversized === true}
                                      />
                                    }
                                    label="Ponadnormatywny"
                                  />

                                  <FormControlLabel
                                    control={
                                      <Switch
                                        name="isInternationalValue"
                                        disabled
                                        checked={values.isInternational === true}
                                      />
                                    }
                                    label="Międzynarodowy"
                                  />
                                </div>
                              </div>

                              <div className={style.PromotionPreview}>
                                <div className={style.ValidityRow}>
                                  <div>Wizytówka będzie ważna do:</div>

                                  <div className={style.Date}>
                                    {moment()
                                      .add(values.period, "years")
                                      .format("DD [-] MM [-] YYYY")}
                                  </div>
                                </div>

                                <div className={style.PromotionTitle}>
                                  Promowanie
                                </div>

                                <div className={style.PreviewSwitches}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        name="highlightValue"
                                        disabled
                                        checked={values.isHighlighted === true}
                                      />
                                    }
                                    label="Wyróżnienie"
                                  />

                                  <FormControlLabel
                                    control={
                                      <Switch
                                        name="recommendedValue"
                                        disabled
                                        checked={values.isRecommended === true}
                                      />
                                    }
                                    label="Polecane TOP 3"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <PaymentModal
                            show={showModalPrice}
                            handleClose={handleCloseModalPrice}
                            values={values}
                            price={price}
                            title="Dodaj nową wizytówkę do serwisu CARTENA"
                            handleSubmit={(submittedValues) => {
                              handleSubmit(submittedValues as FormValues, price);
                              handleCloseModalPrice();
                            }}
                          />
                        </div>
                      </>
                    );
                  }}
                </Formik>
              </>
            ) : (
              <div>
                Brak uprawnień{" "}
                <NavLink
                  key="signin"
                  exact
                  to="/signin"
                  className={style.MenuItem}
                >
                  Zaloguj się jako Administrator.
                </NavLink>
              </div>
            )}
  </PageLayout>
  );
};

export default AdminAddCompany;